import { dateVoService } from '../../value-object/DateVO'

// プロジェクトのステータス（enum 禁止のため as const オブジェクト＋ユニオン型で表現）。
export const ProjectStatus = {
  STANDBY: 'STANDBY',
  INPROGRESS: 'INPROGRESS',
  SUSPENDED: 'SUSPENDED',
  COMPLETED: 'COMPLETED',
} as const
// enum 代替（as const）のため、値と同名の型を意図的に宣言する。
// eslint-disable-next-line no-redeclare
export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus]

// WBSアイテムコードの採番形式。
export const WbsItemCodeFormat = {
  RANDOM: 'RANDOM',
  SEQUENTIAL: 'SEQUENTIAL',
} as const
// enum 代替（as const）のため、値と同名の型を意図的に宣言する。
// eslint-disable-next-line no-redeclare
export type WbsItemCodeFormat =
  (typeof WbsItemCodeFormat)[keyof typeof WbsItemCodeFormat]

// TODO: implement further fields.
export type ProjectEntity = {
  uuid: string
  code: string
  displayName: string
  iconUrl: string | undefined
  status: ProjectStatus
}

// プロジェクトが削除可能かを判定する（STANDBY のステータスのみ削除可）。
export const isProjectDeletable = (
  status: ProjectStatus | undefined
): boolean => status === ProjectStatus.STANDBY

// プロジェクトがクローズ状態（完了・中断）かを判定する。
export const isProjectClosed = (status: ProjectStatus | undefined): boolean =>
  status === ProjectStatus.COMPLETED || status === ProjectStatus.SUSPENDED

// 新規プロジェクトの既定値（作成時はステータス STANDBY・採番形式 RANDOM）。
export const DEFAULT_PROJECT_STATUS: ProjectStatus = ProjectStatus.STANDBY
export const DEFAULT_WBS_ITEM_CODE_FORMAT: WbsItemCodeFormat =
  WbsItemCodeFormat.RANDOM

// 項目の編集可否ルール（isNew: 新規作成中かどうか）。
// コードと WBSアイテムコード採番形式は作成時のみ設定可能で、作成後は変更できない。
export const isProjectCodeEditable = (isNew: boolean): boolean => isNew
export const isWbsItemCodeFormatEditable = (isNew: boolean): boolean => isNew
// ステータスは作成時は STANDBY 固定で、作成後にのみ変更できる。
export const isProjectStatusEditable = (isNew: boolean): boolean => !isNew

// 各項目の最大文字数。
export const PROJECT_CODE_MAX_LENGTH = 20
export const PROJECT_DISPLAY_NAME_MAX_LENGTH = 256
export const PROJECT_OFFICIAL_NAME_MAX_LENGTH = 128
export const PROJECT_MAIN_EMAIL_MAX_LENGTH = 256

// ステータスに対するプロジェクト開始日の妥当性。
// STANDBY は今日以降、それ以外は今日以前であること。
export const isValidProjectStartDate = (
  startDate: string | number | Date | undefined,
  status: ProjectStatus | undefined
): boolean => {
  if (!status) return false
  const start = dateVoService.construct(startDate)
  if (!dateVoService.isValid(start)) return false

  const today = dateVoService.construct('TODAY')
  const diff = dateVoService.diff(start, today)
  return (
    (ProjectStatus.STANDBY === status && diff >= 0) ||
    (ProjectStatus.STANDBY !== status && diff <= 0)
  )
}

// ステータスに対するプロジェクト終了日の妥当性。
// COMPLETED は今日以前、それ以外は今日以降であること。
export const isValidProjectEndDate = (
  endDate: string | number | Date | undefined,
  status: ProjectStatus | undefined
): boolean => {
  if (!status) return false
  const end = dateVoService.construct(endDate)
  if (!dateVoService.isValid(end)) return false

  const today = dateVoService.construct('TODAY')
  const diff = dateVoService.diff(end, today)
  return (
    (ProjectStatus.COMPLETED !== status && diff >= 0) ||
    (ProjectStatus.COMPLETED === status && diff <= 0)
  )
}
